import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Country } from "../countries";

export interface KycDraft {
  phoneNumber?: string;
  basicDetails?: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    dateOfBirth?: string;
    nationality?: string;
  };
  residentialAddress?: {
    street?: string;
    subStreet?: string;
    locality?: string;
    postalCode?: string;
    administrativeArea?: string;
    country?: string;
  };
  taxIdentifiers?: { type: "ssn"; value: string }[];
  questionnaires?: { type: string; answers: Record<string, string> }[];
}

export type KycDraftFieldPath =
  | "phoneNumber"
  | "basicDetails.firstName"
  | "basicDetails.middleName"
  | "basicDetails.lastName"
  | "basicDetails.dateOfBirth"
  | "basicDetails.nationality"
  | "residentialAddress.street"
  | "residentialAddress.subStreet"
  | "residentialAddress.locality"
  | "residentialAddress.postalCode"
  | "residentialAddress.administrativeArea"
  | "residentialAddress.country"
  | "taxIdentifiers.ssn"
  | "questionnaires.customerDueDiligence.employmentStatus"
  | "questionnaires.customerDueDiligence.accountPurpose"
  | "questionnaires.customerDueDiligence.grossAnnualIncome"
  | "questionnaires.customerDueDiligence.sourceOfWealth"
  | "questionnaires.customerDueDiligence.transactionFrequencyPerMonth"
  | "questionnaires.customerDueDiligence.expectedTransactionAmountPerMonth"
  | "questionnaires.enhancedDueDiligence.profession"
  | "questionnaires.enhancedDueDiligence.netWorth";

export interface KycDraftContextValue {
  draft: KycDraft;
  updateField: (path: KycDraftFieldPath, value: string) => void;
  countries: Country[] | null;
  reloadCountries: () => void;
}

const KycDraftContext = createContext<KycDraftContextValue | null>(null);

function upsertQuestionnaireAnswer(
  draft: KycDraft,
  type: string,
  field: string,
  value: string,
): KycDraft {
  const existing = draft.questionnaires ?? [];
  const index = existing.findIndex((entry) => entry.type === type);
  if (index === -1) {
    return { ...draft, questionnaires: [...existing, { type, answers: { [field]: value } }] };
  }
  const current = existing[index];
  if (!current) {
    return { ...draft, questionnaires: [...existing, { type, answers: { [field]: value } }] };
  }
  const next = existing.slice();
  next[index] = { type: current.type, answers: { ...current.answers, [field]: value } };
  return { ...draft, questionnaires: next };
}

export function questionnaireAnswer(draft: KycDraft, type: string, field: string): string {
  return draft.questionnaires?.find((entry) => entry.type === type)?.answers[field] ?? "";
}

export function applyDraftField(draft: KycDraft, path: KycDraftFieldPath, value: string): KycDraft {
  if (path === "phoneNumber") {
    return { ...draft, phoneNumber: value };
  }
  if (path === "taxIdentifiers.ssn") {
    return { ...draft, taxIdentifiers: [{ type: "ssn", value }] };
  }
  if (path.startsWith("basicDetails.")) {
    const field = path.slice("basicDetails.".length) as keyof NonNullable<KycDraft["basicDetails"]>;
    return { ...draft, basicDetails: { ...draft.basicDetails, [field]: value } };
  }
  if (path.startsWith("questionnaires.")) {
    const rest = path.slice("questionnaires.".length);
    const separator = rest.indexOf(".");
    if (separator === -1) {
      throw new Error(`applyDraftField: unknown path "${path}"`);
    }
    const type = rest.slice(0, separator);
    const field = rest.slice(separator + 1);
    return upsertQuestionnaireAnswer(draft, type, field, value);
  }
  if (!path.startsWith("residentialAddress.")) {
    throw new Error(`applyDraftField: unknown path "${path}"`);
  }
  const field = path.slice("residentialAddress.".length) as keyof NonNullable<
    KycDraft["residentialAddress"]
  >;
  return { ...draft, residentialAddress: { ...draft.residentialAddress, [field]: value } };
}

const noop = () => {};

export const KycDraftProvider = ({
  countries = null,
  reloadCountries = noop,
  children,
}: {
  countries?: Country[] | null;
  reloadCountries?: () => void;
  children: ReactNode;
}) => {
  const [draft, setDraft] = useState<KycDraft>({});
  const updateField = useCallback((path: KycDraftFieldPath, value: string) => {
    setDraft((current) => applyDraftField(current, path, value));
  }, []);
  const value = useMemo(
    () => ({ draft, updateField, countries, reloadCountries }),
    [draft, updateField, countries, reloadCountries],
  );
  return createElement(KycDraftContext.Provider, { value }, children);
};

export function useKycDraft(): KycDraftContextValue {
  const context = useContext(KycDraftContext);
  if (!context) {
    throw new Error("useKycDraft must be used within KycDraftProvider");
  }
  return context;
}
